import create from 'zustand';

interface Notification {
  id: string;
  message: string;
  timestamp: string; // ISO string for consistent formatting
  isNew: boolean; // Indicates whether the notification is unread
  type?: string; // Type of notification (e.g., 'game_analysis', 'info')
  requestId?: string; // Request ID for tracking
  userId?: string; // User ID associated with the notification
  senderName?: string; // Display name for inbox: who sent the message
}

interface NotificationStore {
  notifications: Notification[];
  gameReviewNotifications: {[id: string]: any[]}; // Keyed by id, value is array of moves
  refetchStudy: boolean;
  setRefetchStudy: (refetchStudy: boolean) => void;
  addNotification: (notification: Notification) => void;
  addGameReviewNotification: (notification: Notification) => void; // New
  updateGameReviewNotification: (notification: Notification) => void; // New
  clearGameReviewNotifications: () => void; // New
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  hasNewNotifications: () => boolean;
  inboxMessages: Notification[];
  addInboxMessage: (notification: Notification) => void;
  hasNewInboxMessages: () => number;
  clearInboxMessages: () => void;
  getLatestNewInboxMessages: () => Notification[] | undefined
  markInboxMessageSeen: (id: string) => void
  
  userRaisedIssue: Notification[];
  addUserRaisedIssue: (notification: Notification) => void;
  getLatestNewUserRaisedIssues: () => Notification[] | undefined;
  markUserRaisedIssueSeen: (id: string) => void;
  hasNewUserRaisedIssue: () => number;
  clearUserRaisedIssue: () => void;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  gameReviewNotifications: {}, // Keyed by id
  refetchStudy: false,
  setRefetchStudy: refetchStudy => set({refetchStudy: refetchStudy}),
  addNotification: notification =>
    set(state => {
      const exists = state.notifications.some(
        notif => notif.id === notification.id,
      );
      if (exists) return state;
      return {
        notifications: [{...notification, isNew: true}, ...state.notifications],
      };
    }),
  addGameReviewNotification: notification =>
    set(state => {
      const move = notification.message;

      // Extract game_uuid from the move data to use as the key
      let gameUuid = notification.id; // fallback to notification id

      if (move && typeof move === 'object') {
        // Try to get game_uuid from the move object
        if (move.game_uuid) {
          gameUuid = move.game_uuid;
        } else if (move.uuid) {
          gameUuid = move.uuid;
        }
      }

      const prevMoves = state.gameReviewNotifications[gameUuid] || [];

      // Ensure moves are accumulated in a single array for the same game_uuid
      let newMoves = [...prevMoves];

      if (move && typeof move === 'object') {
        // Check if this move already exists (by move_index if available)
        // const existingMoveIndex =
        //   move.move_index !== undefined
        //     ? prevMoves.findIndex(m => m.move_index === move.move_index)
        //     : -1;

        // if (existingMoveIndex === -1) {
        // Add new move if it doesn't exist
        newMoves.push(move);
        // } else {
        //   // Update existing move
        //   newMoves[existingMoveIndex] = move;
        // }

        // Sort by move_index to maintain order
        newMoves.sort((a, b) => (a.move_index || 0) - (b.move_index || 0));
      } else {
        // For non-object moves, just add to array
        newMoves.push(move);
      }

      console.log(
        'addGameReviewNotification - Accumulating moves for game_uuid:',
        gameUuid,
        {
          totalMoves: newMoves.length,
          gameReviewNotifications: {
            ...state.gameReviewNotifications,
            [gameUuid]: newMoves,
          },
        },
      );

      return {
        gameReviewNotifications: {
          ...state.gameReviewNotifications,
          [gameUuid]: newMoves,
        },
      };
    }),
  updateGameReviewNotification: notification =>
    set(state => {
      const move = notification.message;

      // Extract game_uuid from the move data to use as the key
      let gameUuid = notification.id; // fallback to notification id

      if (move && typeof move === 'object') {
        // Try to get game_uuid from the move object
        if (move.game_uuid) {
          gameUuid = move.game_uuid;
        } else if (move.uuid) {
          gameUuid = move.uuid;
        }
      }

      const prevMoves = state.gameReviewNotifications[gameUuid] || [];

      // Ensure moves are accumulated in a single array for the same game_uuid
      let newMoves = [...prevMoves];

      if (move && typeof move === 'object') {
        // Check if this move already exists (by move_index if available)
        // const existingMoveIndex =
        //   move.move_index !== undefined
        //     ? prevMoves.findIndex(m => m.move_index === move.move_index)
        //     : -1;

        // if (existingMoveIndex === -1) {
        // Add new move if it doesn't exist
        newMoves.push(move);
        // } else {
        //   // Update existing move
        //   newMoves[existingMoveIndex] = move;
        // }

        // Sort by move_index to maintain order
        // newMoves.sort((a, b) => (a.move_index || 0) - (b.move_index || 0));
      } else {
        // For non-object moves, just add to array
        newMoves.push(move);
      }

      console.log(
        'updateGameReviewNotification - Accumulating moves for game_uuid:',
        gameUuid,
        JSON.stringify(
          {
            totalMoves: newMoves.length,
            gameReviewNotifications: {
              ...state.gameReviewNotifications,
              [gameUuid]: newMoves,
            },
          },
          null,
          2,
        ),
      );

      return {
        gameReviewNotifications: {
          ...state.gameReviewNotifications,
          [gameUuid]: newMoves,
        },
      };
    }),
  clearGameReviewNotifications: () => set({gameReviewNotifications: {}}),
  markAllAsRead: () =>
    set(state => ({
      notifications: state.notifications.map(notif => ({
        ...notif,
        isNew: false,
      })),
    })),
  removeNotification: id =>
    set(state => ({
      notifications: state.notifications.filter(notif => notif.id !== id),
    })),
  hasNewNotifications: () => get().notifications.some(notif => notif.isNew),

  inboxMessages: [],
  addInboxMessage: notification =>
    set(state => {
      const exists = state.inboxMessages.some(n => n.id === notification.id);
      if (exists) return state;
      return {
        inboxMessages: [{...notification, isNew: true}, ...state.inboxMessages],
      };
    }),

  getLatestNewInboxMessages: () =>
    get().inboxMessages.filter(i => i.isNew),

  markInboxMessageSeen: id =>
    set(state => ({
      inboxMessages: state.inboxMessages.map(m =>
        m.id === id ? { ...m, isNew: false } : m
      ),
    })),
  hasNewInboxMessages: () => get().inboxMessages.length,
  clearInboxMessages: () => set({inboxMessages: []}),
  userRaisedIssue: [],
  addUserRaisedIssue: notification =>
    set(state => {
      const existing = state.userRaisedIssue || [];

      const updated = existing.map(n =>
        n.id === notification.id
          ? { ...n, isNew: false } // 👈 mark old as seen
          : n
      );

      return {
        userRaisedIssue: [
          { ...notification, isNew: true }, // 👈 add latest on top
          ...updated,
        ],
      };
    }),
  getLatestNewUserRaisedIssues: () =>
    get().userRaisedIssue.filter(i => i.isNew),

  markUserRaisedIssueSeen: id =>
    set(state => ({
      userRaisedIssue: state.userRaisedIssue.map(i =>
        i.id === id ? { ...i, isNew: false } : i
      ),
    })),
  hasNewUserRaisedIssue: () => get().userRaisedIssue.length,
  clearUserRaisedIssue: () => set({userRaisedIssue: []}),
}));
